import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Retos.css";
import { getAuthHeader } from "../../utils/auth";
import { getApiBaseUrl } from "../../utils/api";

const API_BASE_URL = "https://jsonplaceholder.typicode.com";

const URL_CHALLENGES = [
  {
    id: "posts-list",
    title: "Listado de posts",
    prompt: "Construye la URL para obtener todos los posts.",
    expectedPath: "/posts",
    validateResponse: (payload) => Array.isArray(payload) && payload.length > 0,
    successHint: "Debe devolver un array con posts.",
  },
  {
    id: "post-detail",
    title: "Detalle del post 1",
    prompt: "Construye la URL del detalle del post con id 1.",
    expectedPath: "/posts/1",
    validateResponse: (payload) => payload && payload.id === 1 && typeof payload.title === "string",
    successHint: "Debe devolver un objeto con id 1.",
  },
  {
    id: "user-posts",
    title: "Posts del usuario 1",
    prompt: "Construye la URL para filtrar solo los posts del usuario 1.",
    expectedPath: "/posts?userId=1",
    validateResponse: (payload) =>
      Array.isArray(payload) && payload.length > 0 && payload.every((item) => item.userId === 1),
    successHint: "Debe devolver una lista donde todos los elementos tengan userId = 1.",
  },
  {
    id: "post-comments",
    title: "Comentarios del post 1",
    prompt: "Construye la URL para obtener los comentarios asociados al post 1.",
    expectedPath: "/posts/1/comments",
    validateResponse: (payload) =>
      Array.isArray(payload) && payload.length > 0 && payload.every((item) => item.postId === 1),
    successHint: "Debe devolver una lista de comentarios con postId = 1.",
  },
];

const normalizePath = (value) => {
  const trimmed = value.trim();
  if (!trimmed) return "";
  return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
};

export default function Reto2() {
  const navigate = useNavigate();
  const apiBaseUrl = getApiBaseUrl();
  const [answers, setAnswers] = useState(
    Object.fromEntries(URL_CHALLENGES.map((challenge) => [challenge.id, ""]))
  );
  const [loading, setLoading] = useState(false);
  const [globalError, setGlobalError] = useState("");
  const [results, setResults] = useState({});

  const evaluatedCount = Object.keys(results).length;

  const score = useMemo(() => {
    const passed = URL_CHALLENGES.filter((challenge) => results[challenge.id]?.passed).length;
    return Math.round((passed / URL_CHALLENGES.length) * 100);
  }, [results]);

  const handleAnswerChange = (challengeId, value) => {
    setAnswers((prev) => ({ ...prev, [challengeId]: value }));
    setResults((prev) => {
      if (!prev[challengeId]) return prev;
      const next = { ...prev };
      delete next[challengeId];
      return next;
    });
    setGlobalError("");
  };

  const validateAll = async () => {
    setLoading(true);
    setGlobalError("");

    const emptyChallenge = URL_CHALLENGES.find((challenge) => !answers[challenge.id].trim());
    if (emptyChallenge) {
      setGlobalError(`Completa la URL de "${emptyChallenge.title}" antes de validar.`);
      setLoading(false);
      return;
    }

    try {
      const validations = await Promise.all(
        URL_CHALLENGES.map(async (challenge) => {
          const normalizedPath = normalizePath(answers[challenge.id]);
          const routeMatches = normalizedPath === challenge.expectedPath;

          try {
            const start = performance.now();
            const response = await fetch(`${API_BASE_URL}${normalizedPath}`);
            const elapsedMs = Math.round(performance.now() - start);
            const text = await response.text();
            let json = null;

            try {
              json = JSON.parse(text);
            } catch (_) {}

            const responseMatches = response.ok && challenge.validateResponse(json);
            const passed = routeMatches && responseMatches;

            return [
              challenge.id,
              {
                normalizedPath,
                routeMatches,
                responseMatches,
                passed,
                status: response.status,
                elapsedMs,
                payload: json ?? text,
                message: passed
                  ? `Ruta correcta y respuesta válida. ${challenge.successHint}`
                  : !routeMatches
                    ? "La ruta no coincide con la esperada para este caso."
                    : "La URL responde, pero la respuesta no cumple lo esperado para el ejercicio.",
              },
            ];
          } catch (_) {
            return [
              challenge.id,
              {
                normalizedPath,
                routeMatches,
                responseMatches: false,
                passed: false,
                status: null,
                elapsedMs: null,
                payload: null,
                message: "No se pudo consultar el endpoint con esa URL.",
              },
            ];
          }
        })
      );

      setResults(Object.fromEntries(validations));
    } finally {
      setLoading(false);
    }
  };

  const resetExercise = () => {
    setAnswers(Object.fromEntries(URL_CHALLENGES.map((challenge) => [challenge.id, ""])));
    setResults({});
    setGlobalError("");
  };

  useEffect(() => {
    if (score < 100 || !apiBaseUrl) return;

    fetch(`${apiBaseUrl}/api/auth/challenge-progress/complete`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeader(),
      },
      body: JSON.stringify({
        challengeId: "reto2",
        title: "Endpoint URL Validator",
        summary: "Has completado el reto API validando todos los endpoints.",
      }),
    }).catch(() => {});
  }, [apiBaseUrl, score]);

  return (
    <div className="dash-page retos-page">
      <div className="retos-container">
        <div className="dash-hero retos-hero">
          <button
            className="dash-btn ghost roadmap"
            onClick={() => navigate("/retos")}
            style={{ marginBottom: 12 }}
          >
            ← Volver a retos
          </button>

          <div>
            <h1>Endpoint URL Validator</h1>
            <p className="muted">
              Construye URLs correctas para distintos casos de uso y valida que cada endpoint
              devuelva exactamente lo esperado.
            </p>

            <div className="dash-quick">
              <div className="quick-card">
                <div className="quick-title">Resultado actual</div>
                <div className="quick-value">{score}%</div>
                <div className="progress">
                  <div className="progress-bar" style={{ width: `${score}%` }} />
                </div>
              </div>

              <div className="quick-card">
                <div className="quick-title">Casos resueltos</div>
                <div className="quick-value">
                  {URL_CHALLENGES.filter((challenge) => results[challenge.id]?.passed).length}/{URL_CHALLENGES.length}
                </div>
                <div className="muted small">La ruta y la respuesta tienen que ser correctas</div>
              </div>

              <div className="quick-card">
                <div className="quick-title">Base URL fija</div>
                <div className="quick-value small">{API_BASE_URL}</div>
                <div className="muted small">Solo completas la ruta del endpoint</div>
              </div>
            </div>
          </div>
        </div>

        <div className="retos-shell">
          <section className="reto-card-2 scenario-card">
            <div className="reto-card-2-head">
              <h2>Escenario</h2>
              <span className="reto-badge">Reto 2 · API · 20 min</span>
            </div>

            <div className="scenario-grid">
              <div className="scenario-block">
                <div className="scenario-label">Tu objetivo</div>
                <p className="muted">
                  Rellenar la ruta correcta para cada caso y comprobar si el endpoint devuelve la
                  información adecuada.
                </p>
              </div>

              <div className="scenario-block">
                <div className="scenario-label">Cómo se puntúa</div>
                <p className="muted">
                  Un caso solo se da por bueno si la ruta es correcta y la respuesta real coincide
                  con el comportamiento esperado.
                </p>
              </div>
            </div>
          </section>

          <div className="reto-panel reto-panel-single">
            <div className="reto-surface">
              <div className="reto-card-2-head">
                <h2>Construye las URLs</h2>
                <span className="pill pill-gray">{evaluatedCount} casos evaluados</span>
              </div>

              {URL_CHALLENGES.map((challenge) => {
                const result = results[challenge.id];

                return (
                  <div key={challenge.id} className="url-task-card">
                    <div className="url-task-head">
                      <div>
                        <div className="case-review-title">{challenge.title}</div>
                        <div className="muted small">{challenge.prompt}</div>
                      </div>
                      <span className={`pill ${result?.passed ? "pill-green" : "pill-gray"}`}>
                        {result ? (result.passed ? "Correcta" : "Revisar") : "Pendiente"}
                      </span>
                    </div>

                    <label className="field-group">
                      <span>Ruta del endpoint</span>
                      <input
                        value={answers[challenge.id]}
                        onChange={(e) => handleAnswerChange(challenge.id, e.target.value)}
                        placeholder={challenge.expectedPath}
                      />
                    </label>

                    {result && (
                      <div className={`choice-feedback ${result.passed ? "pass" : "fail"}`}>
                        <strong>{result.passed ? "Validación superada." : "Validación incorrecta."}</strong>
                        <div>{result.message}</div>
                        <div className="muted small">
                          Ruta enviada: {result.normalizedPath || "vacía"}
                          {result.status ? ` · Status: ${result.status}` : ""}
                          {result.elapsedMs ? ` · Tiempo: ${result.elapsedMs} ms` : ""}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {globalError && <p className="error">{globalError}</p>}

              <div className="reto-actions split">
                <button className="reto-btn reto-btn-inline" onClick={validateAll} disabled={loading}>
                  {loading ? "Validando..." : "Validar URLs"}
                </button>
                <button className="mini-btn" onClick={resetExercise} type="button">
                  Reiniciar ejercicio
                </button>
              </div>
            </div>
          </div>

          <section className="reto-card-2 wide">
            <div className="reto-card-2-head">
              <h2>Comprobaciones del ejercicio</h2>
              <span className="muted small">Qué validamos en cada caso</span>
            </div>

            <div className="validation-list">
              {URL_CHALLENGES.map((challenge) => {
                const result = results[challenge.id];
                return (
                  <div
                    key={challenge.id}
                    className={`validation-item ${
                      result ? (result.passed ? "pass" : "fail") : "pending"
                    }`}
                  >
                    <div>
                      <strong>{challenge.title}</strong>
                      <div className="muted small">{challenge.successHint}</div>
                    </div>
                    <span className="pill">
                      {result ? (result.passed ? "OK" : "Fallo") : "Pendiente"}
                    </span>
                  </div>
                );
              })}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
